
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Target, Building2, Crown, GraduationCap, User } from 'lucide-react';
import { useAppTranslations } from '@/hooks/useAppTranslations';

type SideTab = {
  to: string;
  label: string;
  Icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  tour: string;
  setup?: string;
};

export function BottomNav() {
  const location = useLocation();
  const { t } = useAppTranslations();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const checkModalState = () => setIsModalOpen(document.body.hasAttribute('data-modal-open'));
    checkModalState();
    const observer = new MutationObserver(checkModalState);
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-modal-open'] });
    return () => observer.disconnect();
  }, []);

  if (isModalOpen) return null;

  // Play is pushed from Profile, so Profile stays highlighted there.
  const activePath = location.pathname.startsWith('/play') ? '/profile' : location.pathname;
  const isActive = (prefix: string) => activePath.startsWith(prefix);
  const isPracticeActive = isActive('/practice');

  const leftTabs: SideTab[] = [
    { to: '/club', label: t.navigation.club, Icon: Building2, tour: 'club-nav', setup: 'nav-club' },
    { to: '/coaching', label: t.navigation.coach, Icon: GraduationCap, tour: 'coach-nav' },
  ];
  const rightTabs: SideTab[] = [
    { to: '/progression', label: t.navigation.progress, Icon: Crown, tour: 'progress-nav' },
    { to: '/profile', label: t.navigation.profile, Icon: User, tour: 'profile-nav' },
  ];

  const renderSideTab = ({ to, label, Icon, tour, setup }: SideTab) => {
    const active = isActive(to);
    return (
      <Link
        key={to}
        to={to}
        data-tour={tour}
        data-setup={setup}
        aria-current={active ? 'page' : undefined}
        className="flex flex-col items-center py-3 px-1 min-w-0 flex-1 transition-all duration-200"
      >
        <Icon
          className="h-5 w-5 mb-1 flex-shrink-0 transition-all duration-200"
          style={{ color: active ? '#2A4494' : '#64748B' }}
        />
        <span
          className="text-[11px] font-medium whitespace-nowrap transition-all duration-200"
          style={{ color: active ? '#2A4494' : '#64748B' }}
        >
          {label}
        </span>
      </Link>
    );
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50">
      {/* Frosted bar: translucent so content blurs underneath */}
      <div
        className="app-chrome rounded-t-2xl flex items-center justify-around relative overflow-visible"
        style={{
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: 'none',
          border: '0.5px solid rgba(15, 23, 42, 0.12)',
          borderBottom: 'none',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {leftTabs.map(renderSideTab)}

        {/* Practice — floating above the pill */}
        <div className="flex flex-col items-center flex-1 relative" style={{ overflow: 'visible' }}>
          <Link
            to="/practice"
            data-tour="practice-nav"
            aria-current={isPracticeActive ? 'page' : undefined}
            className="flex flex-col items-center cursor-pointer"
            style={{ overflow: 'visible' }}
          >
            <div
              className="flex items-center justify-center rounded-full transition-all duration-200 relative"
              style={{
                width: 60,
                height: 60,
                marginTop: -30,
                background: isPracticeActive
                  ? 'linear-gradient(135deg, #3D5CC7 0%, #2A4494 60%, #1E3270 100%)'
                  : 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)',
                boxShadow: isPracticeActive
                  ? '0 0 0 3px rgba(42,68,148,0.4), 0 8px 28px rgba(42,68,148,0.6)'
                  : '0 4px 20px rgba(42,68,148,0.5)',
                transform: isPracticeActive ? 'scale(1.06)' : 'scale(1)',
                border: '2px solid rgba(255,255,255,0.15)',
              }}
            >
              <Target className="h-6 w-6 text-white" />
            </div>
            <span
              className="text-[11px] font-semibold mt-1.5 mb-3 whitespace-nowrap transition-all duration-200"
              style={{ color: isPracticeActive ? '#2A4494' : '#64748B' }}
            >
              {t.navigation.practice}
            </span>
          </Link>
        </div>

        {rightTabs.map(renderSideTab)}
      </div>
    </nav>
  );
}
